import {
  Component,
  Element,
  Prop,
  h,
  Host,
  Listen,
  State,
  Watch,
  forceUpdate,
  EventEmitter,
  Event,
} from '@stencil/core';
import { uuidv4 } from '@/globals/helpers';
import { angle_down, arrow_left, arrow_right } from 'pn-design-assets/pn-assets/icons.js';

/**
 * @deprecated This component will be removed in v8. Please use the `pn-tablist` instead.
 * @see {@link https://portal.postnord.com/web-components/?path=/docs/components-navigation-tablist--docs} Use `pn-tablist` instead.
 *
 * @slot dropdown-item - Place your `pn-page-nav-dropdown-item` components here.
 */
@Component({
  tag: 'pn-page-nav',
  styleUrl: 'pn-page-nav.scss',
})
export class PnPageNav {
  mo: MutationObserver;

  navContainer: HTMLUListElement;
  navWrapper: HTMLElement;
  navItems: HTMLPnPageNavItemElement[] = [];

  dropdownButton: HTMLElement;
  dropdownEl: HTMLElement;
  dropdownItems: HTMLPnPageNavItemElement[];
  dropdownEls: HTMLPnPageNavDropdownItemElement[];

  eventHandler = this.keyboardEvents.bind(this);
  globalEventHandler = this.globalEvents.bind(this);

  activeBg: HTMLDivElement;
  hoverBg: HTMLDivElement;

  scrollRegistered: boolean = false;

  @Element() hostElement: HTMLElement;

  @State() currentSelection: HTMLElement;
  @State() showScrollArrows: boolean = false;
  @State() showLeftArrow: boolean = false;
  @State() showRightArrow: boolean = false;
  @State() dropdownOpen: boolean = false;
  @State() dropdownLinks: Array<string> = [];
  @State() dropdownActive: boolean = false;

  /** Currently active menu item value */
  @Prop({ mutable: true }) value: string;
  /** Pass a string which will be the text on the dropdown button. */
  @Prop() dropdown: string | boolean = false;
  /** Set a unique HTML ID. */
  @Prop() navid: string = `pn-page-nav-${uuidv4()}`;

  /** Emits the value of the selected item. */
  @Event() navchange: EventEmitter<string>;

  @Listen('itemselection')
  changeHandler({ target }) {
    this.currentSelection = target.closest('pn-page-nav-item');
    if (target.value) this.value = target.value;

    if (this.dropdownOpen) this.dropdownOpen = false;
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.rerender();
  }

  @Watch('value')
  valueHandler() {
    if (!this.value) this.currentSelection = null;
    this.calcHighlight(this.currentSelection, this.activeBg);

    this.navchange.emit(this.value);

    if (!this.dropdownActive) return;
    this.isDropdownItemActive();
  }

  @Watch('dropdownOpen')
  dropdownHandler() {
    if (this.dropdownOpen) {
      requestAnimationFrame(() => {
        this.addGlobalEventListeners();
      });

      return;
    }

    this.removeGlobalEventListeners();
  }

  /* ---------------------------------------LIFECYCLE--------------------------------------- */
  componentWillLoad() {
    if (!this.dropdown) return;
    this.dropdownEls = Array.from(this.hostElement.querySelectorAll('pn-page-nav-dropdown-item'));
    if (this.dropdownEls.length) {
      this.dropdownActive = true;
      this.initiateDropdown();
    }
  }

  componentDidLoad() {
    if (this.mo) this.mo.disconnect();
    this.mo = new MutationObserver(() => {
      forceUpdate(this.hostElement);
      this.setActiveNavItem();
      this.rerender();
    });
    this.mo.observe(this.hostElement, { childList: true, subtree: true });

    this.navWrapper = this.hostElement.querySelector('.pn-page-nav');
    this.navContainer = this.hostElement.querySelector('.pn-page-nav-items');
    this.activeBg = this.hostElement.querySelector('.pn-pn-active');
    this.hoverBg = this.hostElement.querySelector('.pn-pn-hover');

    this.hostElement.addEventListener('mouseover', ({ target }) =>
      this.calcHighlight(target as HTMLElement, this.hoverBg),
    );

    this.setActiveNavItem();
    this.rerender();
  }
  /* ---------------------------------------/LIFECYCLE--------------------------------------- */

  setActiveNavItem() {
    this.navItems = Array.from(this.hostElement.querySelectorAll('pn-page-nav-item'));
    this.navItems.forEach(navItemEl => {
      if (this.value === navItemEl.value) {
        this.currentSelection = navItemEl;
      } else {
        navItemEl.removeAttribute('selected');
      }

      navItemEl
        .querySelector('a')
        .addEventListener('focus', ({ target }) => this.calcHighlight(target as HTMLElement, this.hoverBg));
    });

    /* -----------------dropdown------------------ */
    if (!this.dropdownActive) return;
    this.dropdownItems = Array.from(this.hostElement.querySelectorAll('.pn-page-nav-dropdown-item'));

    //Check active state on each item
    this.isDropdownItemActive();

    //Store all values to check if dropdown button should be active
    this.dropdownLinks = this.dropdownItems.map((el, i) => {
      el.setAttribute('data-index', `${i}`);
      return (el.closest('pn-page-nav-dropdown-item') as any).value;
    });
    /* -----------------/dropdown------------------ */
  }

  rerender() {
    requestAnimationFrame(() => {
      this.calcHighlight(this.currentSelection, this.activeBg);
      this.scrollArrowRender();
    });
  }

  /*---------------------------------------HIGHLIGHT LOGIC-------------------------------------------*/
  calcHighlight(el: HTMLElement, bgEl: HTMLElement) {
    if (!el?.closest('pn-page-nav-item')) {
      bgEl?.classList.add('hidden');
      return;
    }

    if (bgEl) bgEl.classList.remove('hidden');

    const elRect = el.closest('pn-page-nav-item').getBoundingClientRect();
    const { left: hostLeft } = this.navContainer.getBoundingClientRect();
    const { left: navLeft, height: navHeight, width: navWidth } = elRect;

    const offset = navLeft - hostLeft + this.navContainer.scrollLeft;

    bgEl.style.setProperty('transform', `translate(${offset}px, -50%`);
    bgEl.style.setProperty('width', `${navWidth}px`);
    bgEl.style.setProperty('height', `${navHeight}px`);
  }
  /*---------------------------------------/HIGHLIGHT LOGIC-------------------------------------------*/

  /*---------------------------------------SCROLL ARROW LOGIC-------------------------------------------*/
  scrollArrowRender() {
    if (!this.navWrapper) return;
    if (this.navWrapper.scrollWidth > this.navWrapper.clientWidth) {
      this.showScrollArrows = true;

      if (!this.scrollRegistered) {
        this.navWrapper.addEventListener('scroll', this.scrollArrowRender.bind(this));
        this.scrollRegistered = true;
      }

      const amountScrolled = Math.round(this.navWrapper.scrollWidth - this.navWrapper.scrollLeft);
      const distanceToEnd = amountScrolled - this.navWrapper.clientWidth;
      const distanceToStart = this.navWrapper.scrollLeft;

      this.showLeftArrow = distanceToStart > 0;
      this.showRightArrow = distanceToEnd > 0;

      return;
    }

    this.showLeftArrow = false;
    this.showRightArrow = false;
    this.showScrollArrows = false;
  }

  scroll(val: number) {
    let amount = this.navWrapper.scrollLeft + val;

    this.navWrapper.scroll({
      left: amount,
      behavior: 'smooth',
    });
  }

  scrollArrowClasses() {
    let classNames = 'pn-pn-arrows ';

    if (this.showLeftArrow) classNames += 'pn-pn-left-visible ';
    if (this.showRightArrow) classNames += 'pn-pn-right-visible ';

    return classNames;
  }
  /*---------------------------------------/SCROLL ARROW LOGIC-------------------------------------------*/

  /* ---------------------------------------DROPDOWN LOGIC--------------------------------------- */

  initiateDropdown() {
    requestAnimationFrame(() => {
      this.dropdownButton = this.hostElement.querySelector('.pn-page-nav-dropdown-button');
      this.dropdownEl = this.hostElement.querySelector('.pn-page-nav-dropdown');

      this.addDropdownEventListeners();
    });
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  isDropdownItemActive() {
    this.dropdownEls.forEach(el => {
      if (el.value && this.value === el.value) {
        el.setAttribute('active', 'true');
        return;
      }

      el.removeAttribute('active');
    });
  }

  /* -----------------events------------------ */
  /* -----------------temporary events------------------ */
  addDropdownEventListeners() {
    this.hostElement.addEventListener('keydown', this.eventHandler);
    this.hostElement.addEventListener('click', this.eventHandler);
  }

  addGlobalEventListeners() {
    const root: Document = this.hostElement.getRootNode() as Document;

    root.addEventListener('focusin', this.globalEventHandler);
    root.addEventListener('keydown', this.globalEventHandler);
    root.addEventListener('click', this.globalEventHandler);
  }

  removeGlobalEventListeners() {
    const root: Document = this.hostElement.getRootNode() as Document;

    root.removeEventListener('focusin', this.globalEventHandler);
    root.removeEventListener('keydown', this.globalEventHandler);
    root.removeEventListener('click', this.globalEventHandler);
  }
  /* -----------------/temporary events------------------ */

  /* -----------------Open dropdown with keyboard------------------ */
  keyboardEvents(e: KeyboardEvent) {
    const target: Element = e.composedPath()[0] as Element;

    // As long as the dropdown is closed, we only want it to react to keyboard input
    // is the user has focus on the button
    if (e.type === 'keydown') {
      if (!this.dropdownOpen && target === this.dropdownButton && ['ArrowUp', 'ArrowDown'].includes(e.code)) {
        this.dropdownOpen = true;

        requestAnimationFrame(() => {
          this.focusNextDropdownItem();
        });
      }
    }
  }
  /* -----------------/Open dropdown with keyboard------------------ */

  globalEvents(e) {
    const target: Element = e.composedPath()[0] as Element;

    if (e.type === 'keydown' && e.code === 'Escape') {
      this.dropdownOpen = false;
      this.dropdownButton.focus();
    }

    if (e.code === 'ArrowDown') this.focusNextDropdownItem();
    if (e.code === 'ArrowUp') this.focusPrevDropdownItem();

    if ((e.type === 'click' || e.type === 'focusin') && !this.dropdownEl.contains(target)) {
      this.dropdownOpen = false;
    }
  }
  /* -----------------/events------------------ */

  /* -----------------focusing------------------ */
  focusNextDropdownItem() {
    const { activeElement }: Document = this.hostElement.getRootNode() as Document;

    if (!activeElement.classList.contains('pn-page-nav-dropdown-item')) {
      this.dropdownItems[0].focus();
      return;
    }

    // focus next item
    const index = parseInt(activeElement.getAttribute('data-index'));

    if (index < this.dropdownItems.length - 1) {
      this.dropdownItems[index + 1].focus();
    }
  }

  focusPrevDropdownItem() {
    const { activeElement }: Document = this.hostElement.getRootNode() as Document;

    if (!activeElement.classList.contains('dropdown-item')) {
      this.dropdownItems[this.dropdownItems.length - 1].focus();
    }

    // focus previous item
    const index = parseInt(activeElement.getAttribute('data-index'));

    if (index > 0) {
      this.dropdownItems[index - 1].focus();
      return;
    }

    this.dropdownButton.focus();
  }
  /* -----------------/focusing------------------ */

  dropdownButtonClasses() {
    let classList = 'pn-page-nav-dropdown-button ';
    if (this.dropdownLinks.includes(this.value)) classList += 'pn-page-nav-dropdown-active ';

    return classList;
  }

  dropdownClasses() {
    let classList = 'pn-page-nav-dropdown ';

    if (this.dropdownOpen) classList += 'pn-page-nav-dropdown-open ';

    return classList;
  }
  /* ---------------------------------------/DROPDOWN LOGIC--------------------------------------- */

  render() {
    return (
      <Host>
        <div class="pn-page-nav-wrapper">
          <nav class="pn-page-nav">
            {this.dropdownActive && (
              <button
                type="button"
                class={this.dropdownButtonClasses()}
                onClick={() => this.toggleDropdown()}
                aria-controls="page-nav-dropdown"
                aria-expanded={`${this.dropdownOpen}`}
              >
                {this.dropdown}
                <pn-icon icon={angle_down} color="white" small></pn-icon>
                <div class="pn-page-nav-divider"></div>
              </button>
            )}

            <ul class="pn-page-nav-items">
              <slot />
              <li class="pn-pn-bg pn-pn-active" role="presentation"></li>
              <li class="pn-pn-bg pn-pn-hover" role="presentation"></li>
            </ul>
          </nav>

          {this.showScrollArrows && (
            <div class={this.scrollArrowClasses()}>
              <button class="pn-pn-arrow-left" onClick={() => this.scroll(-120)} tabindex="-1">
                <pn-icon icon={arrow_left} color="white"></pn-icon>
              </button>

              <button class="pn-pn-arrow-right" onClick={() => this.scroll(120)} tabindex="-1">
                <pn-icon icon={arrow_right} color="blue700"></pn-icon>
              </button>
            </div>
          )}
        </div>

        {this.dropdownActive && (
          <ul id={this.navid} class={this.dropdownClasses()}>
            <slot name="dropdown-item" />
          </ul>
        )}
      </Host>
    );
  }
}
